import type {
  SessionHearingParticipant,
  VoiceSessionResponse,
  DisconnectReason,
} from 'features/voice-session/interfaces';
import type { VoiceSessionHearingParticipant } from 'features/voice-meeting/interfaces';

/**
 * Adapts a Chime hearing participant to the format used in the application
 * @param hearingParticipant - The hearing participant from the voice session
 * @returns Adapted hearing participant
 */
export const adaptHearing = (
  hearingParticipant: SessionHearingParticipant
): VoiceSessionHearingParticipant => {
  const phoneNumber = getHearingPhoneNumber(hearingParticipant);

  return {
    id: hearingParticipant.id,
    state: hearingParticipant.state,
    disconnectReason: (hearingParticipant.disconnectReason ||
      '') as DisconnectReason,
    phoneNumber,
    direction: hearingParticipant.direction,
  };
};

/**
 * Returns the appropriate phone number from a hearing participant based on direction
 * @param hearingParticipant - The hearing participant object
 * @returns The phone number to display
 */
export const getHearingPhoneNumber = (
  hearingParticipant: SessionHearingParticipant
): string =>
  hearingParticipant.direction === 'outbound'
    ? hearingParticipant.dialString
    : hearingParticipant.fromPhoneNumber;

/**
 * Analyzes voice session data to determine if a call is inbound or outbound
 * @param voiceSession - The voice session response data
 * @param hearingId - Optional ID of the hearing participant to check
 * @returns 'inbound' or 'outbound'
 */
export const determineCallDirection = (
  voiceSession: VoiceSessionResponse,
  hearingId?: string
): 'inbound' | 'outbound' => {
  // If the call was previously marked as outbound, it stays outbound
  if (voiceSession.previousDirection === 'outbound') {
    return 'outbound';
  }

  // If hearingId is provided, find that specific participant
  if (hearingId) {
    const participant = voiceSession.participants.find(
      (p) => p.type === 'hearing' && p.id === hearingId
    );

    if (participant?.direction) {
      return participant.direction;
    }
  }

  // Otherwise find the first hearing participant
  const hearingParticipant = voiceSession.participants.find(
    (p) => p.type === 'hearing'
  );

  // Return the direction if set, default to outbound
  return hearingParticipant?.direction || 'outbound';
};
